import { useEffect, useState } from "react";
import api, { fmtTRY, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, CheckCircle2, AlertTriangle, Receipt, FileSpreadsheet } from "lucide-react";
import { downloadFile } from "../lib/download";
import DatePicker from "../components/common/DatePicker";

const STATUS_LABELS = {
  bekliyor: { label: "Bekliyor", color: "bg-amber-500 text-slate-900" },
  tahsil: { label: "Tahsil Edildi", color: "bg-emerald-600 text-white" },
  gecikti: { label: "Gecikti", color: "bg-rose-600 text-white" },
};

const empty = { note_type: "senet", amount: 0, due_date: "", payer: "", bank: "", customer_id: "", customer_name: "", notes: "" };

export default function NotesPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [customers, setCustomers] = useState([]);

  const load = () => {
    const url = tab === "gecikti" ? "/notes?overdue_only=true" : tab === "all" ? "/notes" : `/notes?status=${tab}`;
    api.get(url).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);

  const submit = async () => {
    if (!form.amount || !form.due_date) return toast.error("Tutar ve vade tarihi zorunlu");
    const payload = { ...form, amount: Number(form.amount) };
    try {
      if (editing) await api.put(`/notes/${editing}`, payload);
      else await api.post("/notes", payload);
      toast.success("Kaydedildi"); setOpen(false); setForm(empty); setEditing(null); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const remove = async (id) => { if (window.confirm("Silinsin mi?")) { await api.delete(`/notes/${id}`); toast.success("Silindi"); load(); } };
  const edit = (n) => { setEditing(n.id); setForm({ ...empty, ...n, due_date: (n.due_date || "").slice(0,10) }); setOpen(true); };
  const openPay = (n) => { setPayFor(n); setPayAmount(n.amount); };
  const pay = async () => {
    try {
      await api.post(`/notes/${payFor.id}/paid`, { paid_amount: Number(payAmount) });
      toast.success("Tahsil edildi, tahsilat kaydı oluşturuldu"); setPayFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const totals = items.reduce((s, n) => {
    if (n.status === "bekliyor") s.bekliyor += Number(n.amount || 0);
    if (n.status === "tahsil") s.tahsil += Number(n.paid_amount || n.amount || 0);
    if (n.overdue) s.overdue += Number(n.amount || 0);
    return s;
  }, { bekliyor: 0, tahsil: 0, overdue: 0 });

  return (
    <div className="space-y-5" data-testid="notes-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Vadeli Tahsilat</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Senet & Çek Takibi</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="gap-2" onClick={() => downloadFile("/exports/notes.xlsx", "senet-cek-listesi.xlsx")} data-testid="export-notes-btn">
            <FileSpreadsheet size={16} /> Excel İndir
          </Button>
          <Button className="bg-[#1B4D3E] hover:bg-[#163e32] text-white gap-2" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-note-btn">
            <Plus size={16} /> Yeni Senet/Çek
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Bekleyen</div><div className="text-2xl font-extrabold text-amber-600">{fmtTRY(totals.bekliyor)}</div></div>
          <div className="h-10 w-10 rounded-xl bg-amber-500 text-white grid place-items-center"><Receipt size={18} /></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Vadesi Geçen</div><div className="text-2xl font-extrabold text-rose-700">{fmtTRY(totals.overdue)}</div></div>
          <div className="h-10 w-10 rounded-xl bg-rose-600 text-white grid place-items-center"><AlertTriangle size={18} /></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Tahsil Edilen</div><div className="text-2xl font-extrabold text-emerald-700">{fmtTRY(totals.tahsil)}</div></div>
          <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white grid place-items-center"><CheckCircle2 size={18} /></div>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="notes-tab-all">Tümü</TabsTrigger>
          <TabsTrigger value="bekliyor" data-testid="notes-tab-bekliyor">Bekleyen</TabsTrigger>
          <TabsTrigger value="gecikti" data-testid="notes-tab-gecikti">Vadesi Geçen</TabsTrigger>
          <TabsTrigger value="tahsil" data-testid="notes-tab-tahsil">Tahsil Edildi</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vade</TableHead>
                <TableHead>Tip</TableHead>
                <TableHead>Müşteri / Keşideci</TableHead>
                <TableHead>Not</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((n) => (
                <TableRow key={n.id} className={n.overdue ? "bg-rose-50/40" : ""} data-testid={`note-row-${n.id}`}>
                  <TableCell className="text-xs">
                    <div className="font-semibold">{fmtDate(n.due_date)}</div>
                    {n.overdue && <div className="text-[10px] text-rose-600 font-bold">+{n.overdue_days} gün gecikti</div>}
                  </TableCell>
                  <TableCell><Badge variant="outline">{(n.note_type || "senet").toUpperCase()}</Badge></TableCell>
                  <TableCell>
                    <div className="text-sm font-semibold">{n.customer_name || n.payer || "—"}</div>
                    {n.bank && <div className="text-[10px] text-slate-500">{n.bank}</div>}
                  </TableCell>
                  <TableCell className="text-xs max-w-xs truncate">{n.notes || "—"}</TableCell>
                  <TableCell className="text-right font-bold">{fmtTRY(n.amount)}</TableCell>
                  <TableCell><Badge className={STATUS_LABELS[n.status]?.color}>{n.overdue ? "Gecikti" : (STATUS_LABELS[n.status]?.label || n.status)}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      {n.status === "bekliyor" && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => openPay(n)} data-testid={`pay-note-${n.id}`}>
                          <CheckCircle2 size={14} /> Tahsil Et
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => edit(n)} data-testid={`note-edit-${n.id}`}><Pencil size={14} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(n.id)} data-testid={`note-delete-${n.id}`}><Trash2 size={14} className="text-rose-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Kayıt yok.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Senet/Çek Düzenle" : "Yeni Senet/Çek"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tip</Label>
              <Select value={form.note_type} onValueChange={(v) => setForm({ ...form, note_type: v })}>
                <SelectTrigger data-testid="note-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="senet">Senet</SelectItem>
                  <SelectItem value="cek">Çek</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Vade Tarihi *</Label><DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testid="note-due" /></div>
            <div className="col-span-2"><Label>Tutar (₺) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="note-amount" /></div>
            <div className="col-span-2">
              <Label>Müşteri</Label>
              <Select value={form.customer_id || ""} onValueChange={(v) => { const c = customers.find((x) => x.id === v); setForm({ ...form, customer_id: v, customer_name: c?.name || "", payer: c?.name || form.payer }); }}>
                <SelectTrigger data-testid="note-customer"><SelectValue placeholder="Seç" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Keşideci</Label><Input value={form.payer} onChange={(e) => setForm({ ...form, payer: e.target.value })} data-testid="note-payer" /></div>
            <div><Label>Banka (Çek)</Label><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} data-testid="note-bank" /></div>
            <div className="col-span-2"><Label>Açıklama</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={submit} data-testid="save-note-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Senet Tahsilatı</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="font-bold">{payFor.customer_name || payFor.payer}</div>
                <div className="text-xs">Vade: {fmtDate(payFor.due_date)} · {fmtTRY(payFor.amount)}</div>
              </div>
              <div><Label>Tahsil Edilen Tutar (₺)</Label><Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="pay-amount" /></div>
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                Tahsilat işaretlendiğinde otomatik "Tahsilat" tipinde finansal işlem oluşturulur.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>İptal</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={pay} data-testid="confirm-pay-btn">Onayla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
